import styled from "@emotion/styled";
import { AnimatePresence, m } from "framer-motion";
import React, { useState, useMemo, useRef, useEffect } from "react";

import useIsomorphicLayoutEffect from "@brexhq/next/src/hooks/useIsomorphicLayoutEffect";

import { DarkModeOverride } from "../../../contexts/Theme";
import {
  OneClickSignInDialog,
  type OneClickSignInDialogProps,
} from "../../modules/OneClickSignIn/OneClickSignInDialog";
import type { INavigationProps, INavMenu } from "../types";

import {
  Backdrop,
  HeaderContainer,
  Logo,
  Nav,
  NavItem,
  NavItemList,
  NavLink,
  NavMenu,
  Submenu,
  DesktopNavLoadingStencil,
} from "./elements";

const DialogWrapper = styled.div(({ theme }) => ({
  display: "block",
  [theme.breakpoints.nav.SML]: {
    display: "none",
  },
}));

export default function DesktopNavigation({
  className,
  currentPageSlug,
  data: { left, right },
  disableLogoLink,
  hideLeft,
  hideRight,
  loading,
  oneClickSignInDialogProps,
  darkModeNav,
}: INavigationProps & {
  oneClickSignInDialogProps?: OneClickSignInDialogProps;
}) {
  const [activeMenuItem, setActiveMenuItem] = useState("");
  const [focusedItem, setFocusedItem] = useState("INITIAL");
  const [focusedSubmenuItemIndex, setFocusedSubmenuItemIndex] = useState(0);
  const [tabbableSubmenuItems, setTabbableSubmenuItems] = useState<
    HTMLElement[]
  >([]);
  const navRef = useRef<HTMLElement>(null);
  const submenuRef = useRef<HTMLDivElement>(null);

  const clearActiveMenuItem = () => setActiveMenuItem("");

  const focusableItems = useMemo(() => {
    return [
      "logo",
      ...[
        ...(hideLeft ? [] : left || []),
        ...(hideRight ? [] : right || []),
      ].map((item) => item.text),
    ];
  }, [left, right, hideLeft, hideRight]);

  const activeSubmenu = useMemo(
    () =>
      activeMenuItem && !hideLeft
        ? (left || []).find(
            (item): item is INavMenu & { _key: string | number } =>
              item._type === "navMenu" && item.text === activeMenuItem,
          )?.submenu
        : undefined,
    [activeMenuItem, left, hideLeft],
  );

  useIsomorphicLayoutEffect(() => {
    if (activeSubmenu) {
      const items = submenuRef.current?.querySelectorAll<HTMLElement>(
        "[data-present='true'] [tabindex='-1']",
      );
      setTabbableSubmenuItems(Array.from(items || []));
    } else {
      setTabbableSubmenuItems([]);
    }
    setFocusedSubmenuItemIndex(0);
  }, [activeSubmenu]);

  useEffect(() => {
    if (navRef.current?.contains(document.activeElement)) {
      // If the navbar contains the current focused element, we can assume that
      // the user is currently tabbing. In this case, we should focus the first
      // submenu item.
      tabbableSubmenuItems[0]?.focus();
    } else if (submenuRef.current?.contains(document.activeElement)) {
      // If the submenu contains the current focued element, we can assume the
      // user is already using the keyboard to navigate through the submenu so
      // we should focus the next item as dictated by focusedSubmenuItemIndex.
      tabbableSubmenuItems[focusedSubmenuItemIndex]?.focus();
    }
    // Otherwise, we can assume that the user is tabbing away from the nav, so
    // we should do nothing.
  }, [focusedSubmenuItemIndex, tabbableSubmenuItems]);

  useEffect(() => {
    // When the page navigates, close the nav.
    setActiveMenuItem("");
  }, [currentPageSlug]);

  const LogoRenderer = (
    <NavItem>
      <Logo
        onActivate={(item) => {
          setActiveMenuItem(item);
          setFocusedItem(item);
        }}
        isFocused={"logo" === focusedItem}
        tabIndex={["INITIAL", "logo"].includes(focusedItem) ? 0 : -1}
        disableLink={disableLogoLink}
      />
    </NavItem>
  );

  const Content = (
    <>
      <Nav
        ref={navRef}
        onMouseOver={(e) => {
          // If the mouseover event fires specifically for the nav element
          // and not any of its children, we can be sure that the mouse is
          // hovering the whitespace of the nav and not any of the links so
          // we should deactivate the active submenu at this point.
          if (e.target === e.currentTarget) {
            clearActiveMenuItem();
          }
        }}
      >
        <NavItemList role="menubar" isFullWidth>
          {LogoRenderer}
          {!hideLeft &&
            (left || []).map((item) => {
              switch (item._type) {
                case "navLink":
                  return (
                    <NavItem key={item._key}>
                      <NavLink
                        {...item}
                        isFocused={item.text === focusedItem}
                        onActivate={clearActiveMenuItem}
                      />
                    </NavItem>
                  );
                case "navMenu":
                  return (
                    <NavItem key={item._key}>
                      <NavMenu
                        text={item.text}
                        isFocused={item.text === focusedItem}
                        isActive={item.text === activeMenuItem}
                        onActivate={setActiveMenuItem}
                      />
                    </NavItem>
                  );
              }
            })}
        </NavItemList>
        <NavItemList role="menubar">
          {!hideRight &&
            (right || []).map((item, i) => (
              <NavItem key={item.text}>
                <NavLink
                  {...item}
                  modifier={
                    i === (right || []).length - 1 ? "primary" : undefined
                  }
                  isFocused={item.text === focusedItem}
                  onActivate={clearActiveMenuItem}
                />
              </NavItem>
            ))}
        </NavItemList>
      </Nav>
      <AnimatePresence>
        {activeSubmenu && (
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 0.2 } }}
          >
            <Submenu
              ref={submenuRef}
              menu={activeMenuItem}
              groups={activeSubmenu}
              darkModeNav={darkModeNav}
            />
          </m.div>
        )}
      </AnimatePresence>
    </>
  );

  return (
    <>
      <Backdrop isActive={!!activeSubmenu} className={className} />
      <DarkModeOverride useDarkMode={darkModeNav ?? false}>
        <HeaderContainer
          className={className}
          role="presentation"
          onMouseLeave={() => clearActiveMenuItem()}
          onKeyDown={(e) => {
            const focusedIndex = focusableItems.findIndex(
              (item) => item === focusedItem,
            );
            const nextIndex =
              focusedIndex === focusableItems.length - 1 ? 0 : focusedIndex + 1;
            const prevIndex =
              focusedIndex === 0 ? focusableItems.length - 1 : focusedIndex - 1;
            switch (e.key) {
              case "Tab":
                clearActiveMenuItem();
                return;
              case "ArrowLeft":
                setFocusedItem(focusableItems[prevIndex]);
                setActiveMenuItem(focusableItems[prevIndex]);
                return;
              case "ArrowRight":
                setFocusedItem(focusableItems[nextIndex]);
                setActiveMenuItem(focusableItems[nextIndex]);
                return;
              case "ArrowDown":
                if (tabbableSubmenuItems.length > 0) {
                  setFocusedSubmenuItemIndex((prevState) =>
                    prevState === tabbableSubmenuItems.length - 1
                      ? 0
                      : prevState + 1,
                  );
                }
                return;
              case "ArrowUp":
                if (tabbableSubmenuItems.length > 0) {
                  setFocusedSubmenuItemIndex((prevState) =>
                    prevState === 0
                      ? tabbableSubmenuItems.length - 1
                      : prevState - 1,
                  );
                }
                return;
            }
          }}
        >
          <header>
            {loading ? (
              <DesktopNavLoadingStencil LogoSlot={LogoRenderer} />
            ) : (
              Content
            )}
          </header>
          {oneClickSignInDialogProps &&
            oneClickSignInDialogProps.shouldShowPopup && (
              // Need to unset dark mode override for the dialog so it doesn't invert colors when dark mode nav is true
              <DarkModeOverride useDarkMode={false}>
                <DialogWrapper>
                  <OneClickSignInDialog
                    shouldShowPopup={oneClickSignInDialogProps.shouldShowPopup}
                    optIn={oneClickSignInDialogProps.optIn}
                    optOut={oneClickSignInDialogProps.optOut}
                  />
                </DialogWrapper>
              </DarkModeOverride>
            )}
        </HeaderContainer>
      </DarkModeOverride>
    </>
  );
}
